import { Seo } from "../components/Seo";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  MapPin,
  Linkedin,
  Twitter,
  ChevronRight,
  Users,
  Target,
  Rocket
} from "lucide-react";

// Anonymous founder block for public site (per v5 spec)
const founderBlock = {
  title: "Founder",
  bio: "SwarmCitadel was founded by an operator with twenty years in institutional fintech, who built the first tokenized gold and silver product ($300M+ assets under management on-chain) and chairs an international standards committee for a regulated industry, now in a third consecutive elected term. SwarmCitadel applies the same architecture that made programmable money auditable to autonomous AI agents: tamper-evident records, policy enforcement, attribution."
};

const values = [
  {
    icon: Target,
    title: "Institutional Grade",
    description: "We build for enterprises. Every feature, every API, every decision is made with institutional requirements in mind."
  },
  {
    icon: Users,
    title: "AI-Native Team",
    description: "Purpose-built AI engineering team delivers alongside human leadership. SwarmCrew agents handle onboarding, DevOps, and support."
  },
  {
    icon: Rocket,
    title: "Ship Fast, Govern First",
    description: "Speed matters, but not at the cost of governance. We enable rapid deployment with built-in institutional controls."
  }
];

// Neutral milestones (no program names)
const milestones = [
  { date: "2024", event: "SwarmCitadel founded. Vision defined." },
  { date: "Q1 2025", event: "Research and architecture design" },
  { date: "Q4 2025", event: "Development begins with AI engineering team" },
  { date: "Q1 2026", event: "Phase 1 complete: 195K lines, 297 tests, 150 endpoints, 39 connectors" },
  { date: "Q2 2026", event: "Accelerator engagements. First design partners onboarding." },
  { date: "Q3 2026", event: "Production deployment. Financial connectors." },
  { date: "Q4 2026", event: "Pre-seed round. 10+ clients across 3 verticals." }
];

export const AboutPage = () => {
  return (
    <div className="pt-24" data-testid="about-page">
      <Seo title="About SwarmCitadel | Compliance Infrastructure for Agentic AI" description="SwarmCitadel builds the compliance operating system for regulated AI agents, aligned with Singapore's Model AI Governance Framework for Agentic AI." path="/about" />
      {/* Hero */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh" />
        <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] bg-swarm-violet/10 rounded-full blur-[150px]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="section-label">About Us</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4">
              Building the Rails
            </h1>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-8">
              <span className="text-gradient-safe">for the Agent Economy</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed mb-8">
              We don't build agents. We build the governance, orchestration, and treasury 
              infrastructure that transforms autonomous AI into enterprise-ready operations.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="section-label">Our Mission</div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              "The governance and operations layer for AI agent swarms, 
              across DeFi, enterprise, marketing, compliance, and DevOps."
            </h2>
            <p className="text-slate-400 text-lg">
              As AI agents become more autonomous and capable, the need for institutional-grade 
              governance becomes critical. We're building that layer.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label">Our Values</div>
            <h2 className="section-title">What We Stand For</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <div key={value.title} className="glass-card p-8 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-swarm-violet/10 flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-swarm-violet" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{value.title}</h3>
                  <p className="text-slate-400">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label">Leadership</div>
            <h2 className="section-title">Our Team</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              A blend of human expertise and AI agents working together.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Anonymous Founder Block */}
            <div className="glass-card p-6 lg:col-span-2">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-swarm-violet to-swarm-cyan flex items-center justify-center text-white text-2xl font-bold mb-4">
                <Users className="w-10 h-10" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">{founderBlock.title}</h3>
              <p className="text-swarm-cyan text-sm mb-3">Founder & CEO</p>
              <p className="text-slate-400 text-sm leading-relaxed">{founderBlock.bio}</p>
            </div>

            {/* AI Team Card */}
            <div className="glass-card p-6 border-swarm-cyan/30">
              <div className="w-20 h-20 rounded-2xl bg-swarm-cyan/10 flex items-center justify-center mb-4">
                <span className="text-3xl">🤖</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">SwarmCrew</h3>
              <p className="text-swarm-cyan text-sm mb-3">AI Operations Team</p>
              <p className="text-slate-400 text-sm mb-4">
                9 AI agents handling onboarding, DevOps, support, and more.
              </p>
              <Link to="/product">
                <Button variant="outline" className="btn-outline w-full">
                  Meet the Crew
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label">Journey</div>
            <h2 className="section-title">Our Roadmap</h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-swarm-border md:-translate-x-1/2" />

            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div
                  key={milestone.date}
                  className={`relative flex items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                    <div className="glass-card p-4 inline-block">
                      <div className="text-swarm-cyan font-mono text-sm">{milestone.date}</div>
                      <div className="text-white">{milestone.event}</div>
                    </div>
                  </div>

                  <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-swarm-violet rounded-full md:-translate-x-1/2 z-10" />

                  <div className="flex-1 ml-12 md:ml-0">
                    <div className="glass-card p-4 md:hidden">
                      <div className="text-swarm-cyan font-mono text-sm">{milestone.date}</div>
                      <div className="text-white">{milestone.event}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-swarm-darker/50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Want to learn more?
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            Get in touch to discuss partnerships, investment, or joining our team.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="btn-glow" data-testid="about-cta-contact">
                Contact Us <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
            <Link to="/investors">
              <Button variant="outline" size="lg" className="btn-outline">
                Investor Portal
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
